import { APP_BASE_HREF, CommonModule, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ErrorHandler, Inject, LOCALE_ID, NgModule, NgZone, Optional, PLATFORM_ID } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DATE_LOCALE, MAT_RIPPLE_GLOBAL_OPTIONS, MatNativeDateModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CheckmarkLoaderModule } from '@commons/contact-modal/checkmark-loader/checkmark-loader.component';
import { MatDayjsDateAdapterModule } from '@commons/dayjs-date-adapter/dayjs-date-adapter.module';
import { ExternalLinkDirectiveModule } from '@commons/directives/external-link/external-link.directive';
import { FooterModule } from '@commons/footer/footer.component';
import { FormRgpdInfoModule } from '@commons/form-rgpd-info/form-rgpd-info.component';
import { PreFooterModule } from '@commons/pre-footer/pre-footer.component';
import { ScrollTopModule } from '@commons/scroll-top/scroll-top.component';
import { StickyNavModule } from '@commons/sticky-nav/sticky-nav.component';
import { ToggleModule } from '@commons/toggle/toggle.component';
import { bugsnagConfig } from '@config/bugsnag.config';
import { ngxsConfig } from '@config/ngxs.config';
import { routerConfig } from '@config/router.config';
import { trackingConfig } from '@config/tracking-config';
import { CejModalComponent } from '@core/components/cej-modal/cej-modal.component';
import { ContactUsComponent } from '@core/components/contact-us/contact-us.component';
import { CoreComponent } from '@core/components/core/core.component';
import { QuicklinkSharedModule } from '@core/ngx-quicklink.shared.module';
import { WizbiiRouterStateSerializer } from '@core/serializer/router.serializer';
import { CustomMetaService } from '@core/services/meta-service/meta.service';
import { environment } from '@environment';
import { FeaturesModule } from '@features/features.module';
import { TransferHttpCacheModule } from '@nguniversal/common';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { NgxsRouterPluginModule, RouterStateSerializer } from '@ngxs/router-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule, Store } from '@ngxs/store';
import { ScrollToModule } from '@nicky-lenaers/ngx-scroll-to';
import { CompanyFormState } from '@stores/company-form/company-form.state';
import { SetConsents } from '@stores/consents/consents.actions';
import { ConsentsState } from '@stores/consents/consents.state';
import { EventOriginState } from '@stores/event/event-origin.state';
import { MaintenanceState } from '@stores/maintenance/maintenance.state';
import { PopupEventState } from '@stores/popup-event/popup-event.state';
import { SecureState } from '@stores/secure/secure.state';
import { VisitedUrlState } from '@stores/visited-url/visited-url.state';
import { PoleEmploiPrivateInterceptor } from '@webservices/job-1j1s/pole-emploi-private.interceptor';
import { PoleEmploiPublicInterceptor } from '@webservices/job-1j1s/pole-emploi-public.interceptor';
import { PoleEmploiRomeInterceptor } from '@webservices/job-1j1s/pole-emploi-rome.interceptor';
import { ALGOLIA_SERVICE_TOKEN, AlgoliaParamsInterface } from '@wizbii/algolia';
import { bugsnagErrorHandlerFactory } from '@wizbii/angular-bugsnag';
import { AosModule, ImageService, ImaginarySrcModule, WINDOW, WINDOW_PROVIDERS } from '@wizbii/angular-utilities';
import { LocaleEnum } from '@wizbii/models';
import { setupTracking, track, trackConversion } from '@wizbii/tracking';
import { Request } from 'express';
import { from } from 'rxjs';
import { filter, map, startWith, switchMap, tap } from 'rxjs/operators';

interface TrackerDicom {
  id: string;
  path: string;
  sent: boolean;
}

const algoliaParams: AlgoliaParamsInterface = {
  applicationId: environment.algolia.applicationId,
  apiKey: environment.algolia.apiKey,
  options: {},
};

@NgModule({
  declarations: [CoreComponent, ContactUsComponent, CejModalComponent],
  imports: [
    BrowserModule.withServerTransition({ appId: environment.applicationId }),
    CommonModule,
    ExternalLinkDirectiveModule,
    BrowserAnimationsModule,
    TransferHttpCacheModule,
    HttpClientModule,
    MatNativeDateModule,
    MatDialogModule,

    FeaturesModule,
    StickyNavModule,
    PreFooterModule,
    FooterModule,
    FormRgpdInfoModule,

    NgxsModule.forRoot(
      [
        ConsentsState,
        SecureState,
        CompanyFormState,
        PopupEventState,
        MaintenanceState,
        VisitedUrlState,
        EventOriginState,
      ],
      ngxsConfig
    ),
    NgxsFormPluginModule.forRoot(),
    NgxsRouterPluginModule.forRoot(),
    NgxsStoragePluginModule.forRoot({ key: [SecureState, CompanyFormState, PopupEventState, VisitedUrlState] }),
    ScrollToModule.forRoot(),
    AosModule.forRoot(),
    QuicklinkSharedModule,
    RouterModule.forRoot([], routerConfig),
    NgxsReduxDevtoolsPluginModule.forRoot({ name: '1 jeune 1 solution', maxAge: 30 }),
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    CheckmarkLoaderModule,
    MatButtonModule,
    MatIconModule,
    MatDayjsDateAdapterModule,
    ScrollTopModule,
    ToggleModule,
    ImaginarySrcModule,
  ],
  providers: [
    WINDOW_PROVIDERS,
    {
      provide: ErrorHandler,
      useFactory: bugsnagErrorHandlerFactory(bugsnagConfig),
    },
    { provide: RouterStateSerializer, useClass: WizbiiRouterStateSerializer },
    { provide: LOCALE_ID, useValue: LocaleEnum.fr_FR },
    { provide: APP_BASE_HREF, useValue: '/' },
    {
      provide: ImageService,
      useValue: new ImageService(environment.api.imaginary),
    },
    {
      provide: ALGOLIA_SERVICE_TOKEN,
      useValue: algoliaParams,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PoleEmploiPublicInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PoleEmploiPrivateInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PoleEmploiRomeInterceptor,
      multi: true,
    },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline', hideRequiredMarker: true } },
    { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
  ],
  bootstrap: [CoreComponent],
})
export class CoreModule {
  // tslint:disable-next-line parameters-max-number
  constructor(
    matIconRegistry: MatIconRegistry,
    domSanitizer: DomSanitizer,
    router: Router,
    metaService: CustomMetaService,
    ngZone: NgZone,
    store: Store,
    @Inject(PLATFORM_ID) platformId: string,
    @Inject(WINDOW) window: any,
    @Optional() @Inject(REQUEST) request: Request
  ) {
    ngZone.runOutsideAngular(() => {
      const domain =
        environment.deployAssetsUrl ??
        (request && isPlatformServer(platformId) ? `${request.protocol}://${request.get('host')}/assets/` : '/assets/');

      matIconRegistry.addSvgIconSet(domSanitizer.bypassSecurityTrustResourceUrl(`${domain}sprite.svg`));

      if (isPlatformBrowser(platformId)) {
        this.initializeTracking(router, window, request, metaService, store);
      }
    });
  }

  private initializeTracking(
    router: Router,
    window: Window,
    request: Request,
    metaService: CustomMetaService,
    store: Store
  ) {
    setupTracking(trackingConfig).then(() => {
      let previousUrl: URL;
      let trackers: TrackerDicom[] = [
        {
          path: '/',
          id: 'DC-3048978/forma0/lp1jeune+unique',
          sent: false,
        },
        {
          path: '/mes-aides',
          id: 'DC-3048978/forma0/lpaides+unique',
          sent: false,
        },
        {
          path: '/emplois/deposer-offre',
          id: 'DC-3048978/forma0/lpoffre+unique',
          sent: false,
        },
        {
          path: '/apprentissage',
          id: 'DC-3048978/forma0/2021-0+unique',
          sent: false,
        },
        {
          path: '/formations',
          id: 'DC-8915311/picct0/lp1jform+unique',
          sent: false,
        },
        {
          path: '/jobs-etudiants',
          id: 'DC-3048978/emplo253/lpetude+unique',
          sent: false,
        },
        {
          path: '/mentorat',
          id: 'DC-8915311/1jeun0/lp1j1m+unique',
          sent: false,
        },
        {
          path: '/contrat-engagement-jeune',
          id: 'DC-3048978/emplo253/lpcej+unique',
          sent: false,
        },
        {
          path: '/trouver-mon-metier',
          id: 'DC-3048978/emplo253/lpmonmet+unique',
          sent: false,
        },
        {
          path: '/creer-mon-cv',
          id: 'DC-3048978/emplo253/lpmoncv+unique',
          sent: false,
        },
        {
          path: '/entreprises',
          id: 'DC-3048978/emplo253/lpformul+unique',
          sent: false,
        },
      ];

      from(window.WizbiiGdpr.getConsents())
        .pipe(
          tap((consents: any) => {
            store.dispatch(new SetConsents(consents));
          }),
          switchMap(() => router.events),
          filter((event) => event instanceof NavigationEnd),
          map((event) => (event as NavigationEnd).urlAfterRedirects),
          startWith(window.location.href.replace(window.origin, '')),
          map((page) => ({ url: window.location.href, page }))
        )
        .subscribe({
          next: (event: any) => {
            const url = new URL(event.url);

            trackers = this.trackDicom(url.pathname, trackers);

            // send pageview only for pages without queryParams
            if (previousUrl?.hash !== url.hash || previousUrl?.pathname !== url.pathname) {
              track('pageview', event);
              previousUrl = url;
            }
            const ogUrl = request
              ? `${request.protocol}://${request.hostname}${request.path}`
              : `${window.location.origin}${window.location.pathname}`;
            metaService.updateOrAddTag({
              property: 'og:url',
              content: ogUrl,
            });
          },
        });
    });
  }

  /**
   *
   * @param { string } pathname current url path
   * @param { TrackerDicom[] } trackers list of Dicom tracker
   * @returns updated list of Dicom tracker
   */
  private trackDicom(pathname: string, trackers: TrackerDicom[]): TrackerDicom[] {
    return trackers
      .filter((item) => !item.sent)
      .map((item) => {
        if (item.path === pathname) {
          trackConversion({
            allow_custom_scripts: true,
            send_to: item.id,
          });
          return { ...item, sent: true };
        }

        return item;
      });
  }
}
